import { HttpTransportType, HubConnectionState, IHttpConnectionOptions } from '@microsoft/signalr';

import { BigWinEvent, CashbackInfo, GameRedirect, LaunchInfoPayload, MaintenanceNotify, PGWheelInfo, PGWheelWinInfo, PGWheelWinTrigger, QueryModel, TrackUserActivity, UserFreebet } from '../../../models';
import { failLaunch, getLaunchToken, getTraceparentHeader, markLaunchStep, resendLaunchCompletion, setLaunchAttr, setLaunchTraceSender } from '../../../services/launch-telemetry';
import { getTokenFromReturnPath, getTopOrigin, guid, jitter, parseQuery } from '../../../helpers';
import { GameCategory, LaunchType } from '../../../enums';
import { BaseLiveConnection } from '../../../services';
import { env } from '../../../environment';
import { RootStore } from '../../../store';

const CONNECT_LADDER = [500, 1000, 2000, 4000, 8000];
const LONG_RETRY_MS = 30000;
const AUTH_RETRY_MS = 300_000;   // 5 min: a dead refresh cannot recover without a relaunch

// The SignalR error carries the useful text, e.g. "...or there is a proxy blocking WebSockets" or
// "WebSocket closed with status code: 1006". The old constant said nothing.
const describeSignalrError = (error: unknown): string => {
  const message = (error as Error)?.message;
  return (typeof message === 'string' && message.trim()) ? message.slice(0, 300) : 'LauncherSocketInitError';
};

class LauncherLiveConnection extends BaseLiveConnection {

  private rootStore?: RootStore;

  private stopped = false;
  private connecting = false;
  private probed = false;
  private reportedInitFailure = false;
  private authBlocked = false;
  private reportedAnonymous = false;
  private wake: (() => void) | null = null;
  private lastOptions: IHttpConnectionOptions | null = null;
  private lastParams: QueryModel | null = null;
  private launchArgs: { partnerId: number; gameId: number; category: GameCategory } | null = null;

  public async startSocketLifecycle(partnerId: number, gameId: number, category: GameCategory, rootStore: RootStore): Promise<void> {
    await this.start(partnerId, gameId, category, rootStore);
    this.setupReconnectionLifecycle();
  }

  private async start(partnerId: number, gameId: number, category: GameCategory, rootStore: RootStore): Promise<void> {
    this.rootStore = rootStore;
    this.launchArgs = { partnerId, gameId, category };
    const socketOptions: IHttpConnectionOptions = {
      // Live read + refresh-on-demand, evaluated on every (re)connect. Was a snapshot
      // `authInfo?.accessToken` captured once at start(), which sent a stale/expired token on
      // reconnect -> server rejected it -> unaddressable socket -> dropped wins.
      accessTokenFactory: () => rootStore.authStore.getFreshAccessToken(),
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets
    };

    await this.connectWithBackoff(socketOptions, { affinityId: guid() });
  }

  /**
   * Every init() builds a fresh HubConnection, so subscriptions do not survive a background reconnect.
   * Re-wire on each successful connect instead of once at start().
   */
  private wireConnection(): void {
    const { rootStore, launchArgs } = this;
    if (!rootStore || !launchArgs) return;
    const { partnerId, gameId, category } = launchArgs;

    rootStore.gameStore.listenEvents();
    rootStore.cashbackStore.listenEvents();
    rootStore.freeSpinStore.listenEvents();
    rootStore.freeDropStore.listenEvents();
    rootStore.boostedJackpotStore.listenEvents();
    rootStore.maintenanceStore.listenEvents();
    this.invokeLaunchInfo(partnerId, gameId, category);

    this.onReconnected(() => {
      this.rootStore?.hubStore.setConnected(true);
      this.rootStore?.userActivityStore.flushPendingActivity();
      this.invokeLaunchInfo(partnerId, gameId, category);
      resendLaunchCompletion();
    });
    this.onReconnecting(() => this.rootStore?.hubStore.setConnected(false));
    this.onClose(() => {
      // withAutomaticReconnect gave up after 0.3 / 0.6 / 2 / 10 / 30 s. Keep trying in the background:
      // the player must not be interrupted, and must not be pushed into a relaunch.
      this.rootStore?.hubStore.setConnected(false);
      failLaunch('socket_closed', 'LauncherSocketClosed');
      void this.connectWithBackoff();
    });
  }

  /**
   * The hub is optional: the game runs on its own socket to slot-lines. A hub that is down costs
   * jackpot, wheel and cashback pushes, not the game. So retry quietly and never block the player.
   */
  private async connectWithBackoff(options?: IHttpConnectionOptions, params?: QueryModel): Promise<void> {
    if (options) this.lastOptions = options;
    if (params) this.lastParams = params;
    if (this.connecting || !this.lastOptions || !this.lastParams) return;

    this.connecting = true;
    try {
      for (let attempt = 0; !this.stopped; attempt++) {
        let lastError: unknown = null;
        await this.init(this.lastOptions, this.lastParams, error => { lastError = error; });

        if (this.connection?.state === HubConnectionState.Connected) {
          if (await this.isAnonymousConnection()) {
            // Drop it now, not on the next pass. The token rides on the socket URL, so this
            // connection can never become authenticated; leaving it Connected makes the state lie
            // and silences the visibility nudge, which only fires on Disconnected.
            await this.stop();
            await this.sleep(jitter(AUTH_RETRY_MS));
            continue;
          }
          markLaunchStep('socket');
          // A fast (cached) launch can finish before the socket is up; tell the hub now, or its later
          // disconnect would be misreported as a launch that died.
          resendLaunchCompletion();
          this.rootStore?.hubStore.setConnected(true);
          // events from before the first connect, or from the outage this connect ends
          this.rootStore?.userActivityStore.flushPendingActivity();
          this.reportedInitFailure = false;
          this.wireConnection();
          return;
        }

        this.rootStore?.hubStore.setConnected(false);

        // One beacon per launch, not one per attempt.
        if (attempt === CONNECT_LADDER.length - 1 && !this.reportedInitFailure) {
          this.reportedInitFailure = true;
          failLaunch('socket_init', describeSignalrError(lastError));
          void this.probeNegotiate();
        }

        const base = this.authBlocked
          ? AUTH_RETRY_MS
          : (attempt < CONNECT_LADDER.length ? CONNECT_LADDER[attempt] : LONG_RETRY_MS);
        await this.sleep(jitter(base));
      }
    } finally {
      this.connecting = false;
    }
  }

  /** Interruptible, so a returning player does not have to wait out a 5-minute auth lane. */
  private sleep(ms: number): Promise<void> {
    return new Promise<void>(resolve => {
      const done = (): void => {
        clearTimeout(timer);
        this.wake = null;
        resolve();
      };
      const timer = setTimeout(done, ms);
      this.wake = done;
    });
  }

  /**
   * LauncherHub is a plain Hub with no [Authorize] and MapHub has no RequireAuthorization(), so a dead
   * token still connects. The socket looks healthy, markLaunchStep('socket') fires, and the hub simply
   * cannot identify the player: no jackpot, wheel, cashback or free-drop pushes, and nothing reports it.
   * That is worse than a visible disconnect, because A3 hub-down mode never triggers.
   */
  private async isAnonymousConnection(): Promise<boolean> {
    const authStore = this.rootStore?.authStore;

    // Cookie-based authorization: the launch carries no tokenInfo by design and the cookie rides the
    // handshake, so an empty token proves nothing. Nothing here is detectable — assume identified.
    if (!authStore?.usesTokenAuth) {
      this.authBlocked = false;
      this.reportedAnonymous = false;
      return false;
    }

    const token = await authStore.getFreshAccessToken();
    // A stale token left behind by a failed refresh is not proof of a live session, and an absent
    // authInfo is not proof of a dead one. refreshFailed is the only signal that separates them.
    const identified = !!token && !authStore.refreshFailed;

    if (identified || !this.rootStore?.gameStore.launchData.data?.isAuthenticated) {
      this.authBlocked = false;
      this.reportedAnonymous = false;
      return false;
    }

    if (!this.reportedAnonymous) {
      this.reportedAnonymous = true;
      failLaunch('socket_anonymous', token ? 'RefreshFailedOnConnect' : 'NoAccessTokenOnConnect');
    }
    this.rootStore?.hubStore.setConnected(false);   // A3 hub-down mode, despite the open socket
    this.authBlocked = true;
    return true;
  }

  /**
   * One probe per launch, only after the ladder failed. We skip negotiate on the real connection, so a
   * failed WS upgrade gives us only close code 1006 — this HTTP call is the only channel that can carry
   * back a `cf-mitigated` header. 200/401 both mean the origin was reached, so the edge did not block us.
   */
  private async probeNegotiate(): Promise<void> {
    if (this.probed) return;
    this.probed = true;
    try {
      const token = await this.rootStore?.authStore.getFreshAccessToken();
      const response = await fetch(`${env.wsUrl}/negotiate?negotiateVersion=1`, {
        method: 'POST',
        credentials: 'omit',
        keepalive: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      setLaunchAttr('launch.negotiate.status', response.status);
      const mitigated = response.headers.get('cf-mitigated');   // Cloudflare sets this when it blocks
      if (mitigated) setLaunchAttr('launch.negotiate.cfMitigated', mitigated);
    } catch {
      setLaunchAttr('launch.negotiate.status', -1);             // never reached the server at all
    }
  }

  private setupReconnectionLifecycle(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.addEventListener('pagehide', this.handlePageHide);
  }

  // The page is going away: stop the background ladder so it cannot outlive the launch.
  private handlePageHide = (event: PageTransitionEvent): void => {
    if (!event?.persisted) {
      this.stopped = true;
      this.wake?.();   // let a sleeping loop notice and exit instead of holding the timer
    }
  }

  protected handleInvokeError(methodName: string, error: Error): void {
    // 401 on a hub invoke means OUR token, not the operator wallet. No popup.
    failLaunch('socket_invoke_error', `${methodName}: ${error?.message ?? ''}`.slice(0, 300));
  }

  private handleVisibilityChange = (): void => {
    if (document.hidden) return;
    // Leave the 5-minute auth lane: the player may have re-authenticated elsewhere.
    this.authBlocked = false;
    // Background timers are throttled while hidden, so the ladder can be asleep when the tab returns.
    this.wake?.();
    // connectWithBackoff is self-guarding: this is a nudge for a dead loop, not a second one.
    if (this.connection?.state === HubConnectionState.Disconnected) {
      void this.connectWithBackoff();
    }
  }

  public invokeLaunchInfo(partnerId: number, gameId: number, category: GameCategory): void {
    const payload: LaunchInfoPayload = {
      gameId,
      category,
      partnerId,
      traceParent: getTraceparentHeader() || undefined,
      launchToken: getLaunchToken() || undefined   // lets the backend dedupe the disconnect span
    };

    // set partner website url in payload from top origin if exists
    const partnerWebsiteUrl = getTopOrigin();
    if (partnerWebsiteUrl) {
      payload.partnerWebsiteUrl = partnerWebsiteUrl;
    }

    // set `partnerToken` in payload if exists in `returnPath` in `LaunchType.BetConstruct` launch type
    const { returnPath } = parseQuery(window.location.search);
    const decodedPath = decodeURIComponent(returnPath);
    const partnerToken = getTokenFromReturnPath(decodedPath);
    if (this.rootStore?.gameStore.launchData.data?.launchType === LaunchType.BetConstruct && partnerToken) {
      payload.partnerToken = partnerToken;
    }

    this.invoke('LaunchInfo', payload);
  }

  // Launch beacon over the live socket; false = not connected, the caller falls back to HTTP.
  public trySendLaunchTrace(payloadJson: string): boolean {
    if (this.connection?.state !== HubConnectionState.Connected) return false;
    this.connection.send('LaunchTrace', payloadJson).catch(() => undefined);
    return true;
  }

  // false = not connected: UserActivityStore keeps the event and sends it on the next (re)connect.
  public trackUserActivity(payload: TrackUserActivity): boolean {
    if (this.connection?.state !== HubConnectionState.Connected) return false;
    this.connection.send('TrackUserActivity', payload).catch(() => undefined);
    return true;
  }

  // Returns false when the socket is not Connected, so a dropped ack can be recorded instead of vanishing.
  public sendAckWheelWin(wheelWinId: number): boolean {
    if (this.connection?.state !== HubConnectionState.Connected) return false;
    this.connection.send('AckWheelWin', wheelWinId).catch(() => undefined);
    return true;
  }

  public invokeGameRedirect(payload: GameRedirect): void {
    this.invoke('Redirect', payload);
  }

  public onCampaignStart(handler: any): void {
    this.on('CampaignStarted', handler);
  }

  public onCampaignCanceled(handler: any): void {
    this.on('CampaignCanceled', handler);
  }

  public onFreeDropProgressUpdate(handler: any): void {
    this.on('CampaignProgressUpdated', handler);
  }

  public onFreeDropAnnounce(handler: any): void {
    this.on('DropAnnounced', handler);
  }

  public onFreeDropPrizeClaimed(handler: any): void {
    this.on('PrizeClaimed', handler);
  }

  public onCashbackStarted(handler: () => void): void {
    this.on('CashbackStarted', handler);
  }

  public onCashbackUpdate(handler: (data: Partial<CashbackInfo>) => void): void {
    this.on('CashbackUpdated', handler);
  }

  public onCashbackEnded(handler: (id: number) => void): void {
    this.on('CashbackEnded', handler);
  }

  public onWheelWidgetStarted(handler: (data: PGWheelInfo) => void): void {
    this.on('WheelWidgetStarted', handler);
  }

  public onWheelWidgetEnded(handler: () => void): void {
    this.on('WheelWidgetEnded', handler);
  }

  public onWheelWinTrigger(handler: (data: PGWheelWinTrigger) => void): void {
    this.on('WheelWinTrigger', handler);
  }

  public onWheelWinInfo(handler: (data: PGWheelWinInfo) => void): void {
    this.on('WheelWinInfo', handler);
  }
 
  public onFreeBetCreated(handler: (data: UserFreebet) => void): void {
    this.on('FreeBetCreated', handler);
  }

  public onBigWin(handler: (data: BigWinEvent) => void): void {
    this.on('BigWin', handler);
  }

  public onMaintenanceNotify(handler: (data: MaintenanceNotify) => void): void {
    this.on('MaintenanceNotify', handler);
  }
}

export const launcherSocket = new LauncherLiveConnection(env.wsUrl);
setLaunchTraceSender(payloadJson => launcherSocket.trySendLaunchTrace(payloadJson));
